import type { AppConfig, PowerMeter } from '@/features/controller/configuration/models/appConfigSchema'
import { PowerMeterModel } from '@/features/controller/configuration/models/appConfigSchema'
import type { ControllerConfigurationModel } from '@/features/controller/configuration/models/controllerConfiguration'
import { isTillquistEnabled } from '@/features/controller/configuration/models/raspiProperties/tillquist'
import type {
  NamedModbusConnection,
  NamedModbusSerialClient,
  RaspiPropertiesConfig,
} from '@/features/controller/configuration/models/raspiProperties/types'
import { NamedModbusTransport, SerialPort } from '@/features/controller/configuration/models/raspiProperties/types'

/** The app.json models the controller talks to on `portName`, rather than on `serialPort.name`. */
const BASIC_POWER_METER_MODELS: ReadonlySet<PowerMeterModel> = new Set([
  PowerMeterModel.iEM3155,
  PowerMeterModel.iEM3255,
  PowerMeterModel.iEM3355,
  PowerMeterModel.iEM3555,
  PowerMeterModel.UPM209,
  PowerMeterModel.UPM307,
])

/**
 * A Generic meter has no port of its own: it adds settings to a meter configured elsewhere. With
 * Tillquist enabled that is the Tillquist meter; otherwise it is almost certainly an auto-detected
 * meter on the basic devices port.
 */
export function isTillquistSettingsMeter(
  powerMeter: Pick<PowerMeter, 'model'>,
  raspiPropertiesConfig: RaspiPropertiesConfig | null,
) {
  return (
    powerMeter.model === PowerMeterModel.Generic &&
    raspiPropertiesConfig !== null &&
    isTillquistEnabled(raspiPropertiesConfig.tillquistConfig)
  )
}

function isBasicPowerMeter(powerMeter: PowerMeter, raspiPropertiesConfig: RaspiPropertiesConfig | null) {
  return (
    BASIC_POWER_METER_MODELS.has(powerMeter.model) ||
    (powerMeter.model === PowerMeterModel.Generic && !isTillquistSettingsMeter(powerMeter, raspiPropertiesConfig))
  )
}

/**
 * Whether app.json declares a meter on the basic devices port: a Schneider or Algodue meter, or a
 * Generic one adjusting an auto-detected meter.
 */
export function hasKnownBasicPowerMeters(
  appConfig: AppConfig | null,
  raspiPropertiesConfig: RaspiPropertiesConfig | null,
) {
  return appConfig?.powerMeters?.some((powerMeter) => isBasicPowerMeter(powerMeter, raspiPropertiesConfig)) ?? false
}

/**
 * Whether the basic devices port is known to be in use. Auto-detected devices leave no trace in the
 * configuration, so only what someone configured counts: an app.json meter on that port, or
 * `portName` moved off the default port 1.
 */
export function isBasicDevicesPortInUse(
  appConfig: AppConfig | null,
  raspiPropertiesConfig: RaspiPropertiesConfig | null,
) {
  return (
    hasKnownBasicPowerMeters(appConfig, raspiPropertiesConfig) ||
    raspiPropertiesConfig?.basicDevicesSerialPort === SerialPort.port2
  )
}

function hasAdvancedDevices(raspiPropertiesConfig: RaspiPropertiesConfig) {
  return (
    isTillquistEnabled(raspiPropertiesConfig.tillquistConfig) ||
    raspiPropertiesConfig.legacyModbusConnections.some((conn) => 'serialPort' in conn && conn.meters.length > 0)
  )
}

function namedSerialClients(raspiPropertiesConfig: RaspiPropertiesConfig): NamedModbusSerialClient[] {
  return raspiPropertiesConfig.legacyModbusConnections
    .filter((conn): conn is NamedModbusConnection => 'namedClient' in conn)
    .map((conn) => conn.namedClient)
    .filter((client): client is NamedModbusSerialClient => client.transport === NamedModbusTransport.serial)
}

/** The tree's messages are English-only, so this mirrors `describeSerialPort`'s labels rather than translating them. */
function describePort(serialPort: SerialPort) {
  return serialPort === SerialPort.port2 ? 'RS-485 ②' : 'RS-485 ①'
}

/**
 * The basic devices are auto-detected by scanning the line at several baud rates, so they need their
 * RS-485 line to themselves. Returns why the configuration can't be saved, or undefined if it can.
 * Advanced devices sharing a line with named connections is not checked: that is not known to break.
 */
export function findSerialPortConflict({
  appConfig,
  raspiPropertiesConfig,
}: Pick<ControllerConfigurationModel, 'appConfig' | 'raspiPropertiesConfig'>): string | undefined {
  if (!raspiPropertiesConfig || !isBasicDevicesPortInUse(appConfig, raspiPropertiesConfig)) {
    return undefined
  }
  const port = raspiPropertiesConfig.basicDevicesSerialPort
  const others = [
    hasAdvancedDevices(raspiPropertiesConfig) &&
      raspiPropertiesConfig.legacySerialPortConfig.serialPort === port &&
      'advanced devices',
    ...namedSerialClients(raspiPropertiesConfig)
      .filter((client) => client.serialPort === port)
      .map((client) => `Modbus serial connection '${client.name}'`),
  ].filter(Boolean)
  if (others.length === 0) {
    return undefined
  }
  return `Basic devices share ${describePort(port)} with ${others.join(', ')}. Basic devices need a port of their own: move one of them to the other port before saving.`
}
